import { useContentfulSingingPortraitSongs } from 'api/contentful/memory'
import { useUserMemoryList } from 'api/goodtrust/user/memory'
import { UploadedItem } from 'components/content/uploadedItem/UploadedItem'
import { GalleryItem } from 'components/gallery/Gallery'
import { MemoryItem } from 'components/memory/memoryItem/MemoryItem'
import { AddButton } from 'components/memory/memoryList/AddButton'
import omit from 'lodash.omit'
import { useRouter } from 'next/router'
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { encodeQuery, FetchResponse } from 'utils/fetcher'
import { PaginationMetaData, useMemoizedPaginationMeta } from 'utils/pagination'
import { ApiMemoryFileResponse, ApiMemoryResponse, ApiType, GenericFunction } from 'utils/types'
import { TFunction } from 'react-i18next'
import { useTranslation } from 'next-i18next'

export type AnimationType = NonNullable<ApiType['MemoryFileResponse']['animation_type']>
export type AnimationDriver = NonNullable<ApiType['MemoryAnimationRequest']['driver_type']>

export type MemoryGalleryTabKey = 'singing' | 'live' | 'all' | 'photo' | 'story'

export type MemorySelectOption = 'single' | 'multi' | 'none'

export type SelectedMemory = ApiMemoryResponse & {
  selectedAnimation: ApiMemoryFileResponse | null
}

export interface MemoryType {
  key: string
  label: ReactNode
  type?: AnimationType | null
}

export interface LifeStoriesCustomTab extends Omit<MemoryType, 'type'> {
  customContent?: ReactNode
  action?: ReactNode
}

export interface MemoryGalleryItem extends GalleryItem {
  data: SelectedMemory | null
}

export interface MemoryGalleryTabContent {
  tab: MemoryType
  content?: MemoryGalleryItem[]
  meta?: PaginationMetaData
  action?: ReactNode
  placeholder?: ReactNode
}

export type ViewedMemory = {
  memoryUuid?: string
  animationUuid?: string | null
}

export const getMemoryGalleryTab: (
  t: TFunction<'common'>
) => Record<MemoryGalleryTabKey, MemoryType> = (t) => {
  return {
    all: {
      key: 'memories-all',
      label: t('common.memory_gallery_tabs.my_library'),
    },
    photo: {
      key: 'memories-photos',
      label: t('common.memory_gallery_tabs.photos'),
    },
    singing: {
      key: 'memories-singing-portraits',
      label: t('common.memory_gallery_tabs.singing_portraits'),
      type: 'SINGING_PORTRAIT',
    },
    live: {
      key: 'memories-live-portraits',
      label: t('common.memory_gallery_tabs.live_portraits'),
      type: 'LIVE_PORTRAIT',
    },
    story: {
      key: 'memories-story-portraits',
      label: t('common.memory_gallery_tabs.story_portraits'),
      type: 'STORY_PORTRAIT',
    },
  }
}

export const AnimatedMemoryGalleryTabs = (t: TFunction<'common', undefined>) =>
  [getMemoryGalleryTab(t).live, getMemoryGalleryTab(t).singing] as const

export const defaultMemoryThumbnailSize = 200 as const

export const incompleteAnimationStatuses: ApiType['MemoryFileResponse']['status'][] = [
  'CREATED',
  'STARTED',
]

export const livePortraitDrivers: AnimationDriver[] = ['FUN', 'NOSTALGIA']

export interface UserMemoryParams {
  types: MemoryType[]
}

export const useUserMemories = (params: UserMemoryParams) => {
  // IMPROVE: decrease the repetetiveness inside this function
  const { t } = useTranslation()
  const memoryGalleryTab = getMemoryGalleryTab(t)
  const { types } = params
  const typeKeys = types.map(({ key }) => key)

  const allMemoriesSwr = useUserMemoryList({
    disabled: !typeKeys.includes(memoryGalleryTab.all.key),
    specification: {
      file_source: 'CUSTOM',
    },
  })
  const allMemoriesMeta = useMemoizedPaginationMeta(allMemoriesSwr)

  const photosSwr = useUserMemoryList({
    disabled: !typeKeys.includes(memoryGalleryTab.photo.key),
    specification: {
      animated: false,
      file_source: 'CUSTOM',
    },
  })
  const photosMeta = useMemoizedPaginationMeta(photosSwr)

  const singingHeadsSwr = useUserMemoryList({
    disabled: !typeKeys.includes(memoryGalleryTab.singing.key),
    specification: {
      animated: true,
      animation_type_filter: 'SINGING_PORTRAIT',
    },
  })
  const singingHeadsMeta = useMemoizedPaginationMeta(singingHeadsSwr)

  const livePortraitsSwr = useUserMemoryList({
    disabled: !typeKeys.includes(memoryGalleryTab.live.key),
    specification: {
      animated: true,
      animation_type_filter: 'LIVE_PORTRAIT',
    },
  })
  const livePortraitsMeta = useMemoizedPaginationMeta(livePortraitsSwr)

  const storyPortraitsSwr = useUserMemoryList({
    disabled: !typeKeys.includes(memoryGalleryTab.story.key),
    specification: {
      animated: true,
      animation_type_filter: 'STORY_PORTRAIT',
    },
  })
  const storyPortraitsMeta = useMemoizedPaginationMeta(storyPortraitsSwr)

  const refreshMemories = useCallback(async () => {
    await allMemoriesSwr.mutate()
    await photosSwr.mutate()
    await singingHeadsSwr.mutate()
    await livePortraitsSwr.mutate()
    await storyPortraitsSwr.mutate()
  }, [allMemoriesSwr, livePortraitsSwr, photosSwr, singingHeadsSwr, storyPortraitsSwr])

  return {
    allMemoriesSwr,
    allMemoriesMeta,
    photosSwr,
    photosMeta,
    singingHeadsSwr,
    singingHeadsMeta,
    livePortraitsSwr,
    livePortraitsMeta,
    storyPortraitsSwr,
    storyPortraitsMeta,
    refreshMemories,
  }
}

export const getIsSameMemory = (memory1: SelectedMemory, memory2: SelectedMemory) => {
  return (
    memory1.id === memory2.id &&
    memory1.selectedAnimation?.file?.uuid === memory2.selectedAnimation?.file?.uuid
  )
}

export const generateMemoryGalleryTabContent = (params: {
  data?: FetchResponse<Pick<ApiType['PaginatedResponseMemoryResponse'], 'items'>>[]
  memoriesBeingUploaded: File[]
  onClick: (memory: SelectedMemory) => void
  selectionMode?: MemorySelectOption
  selectedMemories?: SelectedMemory[]
  animationType?: ApiType['MemoryFileResponse']['animation_type'] | null
  uploadNewMemories?: GenericFunction
}) => {
  const {
    data,
    memoriesBeingUploaded,
    onClick,
    selectionMode,
    selectedMemories,
    animationType,
    uploadNewMemories,
  } = params

  const newItems: MemoryGalleryItem[] = []

  memoriesBeingUploaded.forEach((file) =>
    newItems.push({
      id: `upload-${file.name}`,
      view: <UploadedItem text={file.name} />,
      width: defaultMemoryThumbnailSize,
      height: defaultMemoryThumbnailSize,
      data: null,
    })
  )

  data?.forEach((page) =>
    page.json?.items?.forEach((memory) => {
      if (animationType) {
        memory.animated_files?.forEach((animation) => {
          if (animation.animation_type !== animationType) return
          const selectedMemory: SelectedMemory = { ...memory, selectedAnimation: animation }

          newItems.push({
            id: `memory-${memory.uuid}-file-${animation.uuid}`,
            created: animation.created ?? undefined,
            view: (
              <MemoryItem
                memory={selectedMemory}
                onClick={onClick}
                selectionMode={selectionMode}
                selected={selectedMemories?.some(
                  (s) => s.id === memory.id && s.selectedAnimation?.uuid === animation.uuid
                )}
              />
            ),
            width: defaultMemoryThumbnailSize,
            height: defaultMemoryThumbnailSize,
            data: selectedMemory,
          })
        })
      } else {
        const selectedMemory: SelectedMemory = { ...memory, selectedAnimation: null }
        newItems.push({
          id: `memory-${memory.uuid}-file-${memory.original_file?.uuid}`,
          created: memory.created ?? undefined,
          view: (
            <MemoryItem
              memory={selectedMemory}
              onClick={onClick}
              selectionMode={selectionMode}
              selected={selectedMemories?.some(
                (s) => s.id === memory.id && s.selectedAnimation === null
              )}
            />
          ),
          width: memory.original_file?.width,
          height: memory.original_file?.height,
          data: selectedMemory,
        })
      }
    })
  )

  if (newItems.length)
    newItems.push({ id: 'add-button', view: <AddButton onClick={uploadNewMemories} />, data: null })

  return newItems
}

export const useViewedMemory = (all: ViewedMemory[]) => {
  const { query, replace, isReady, pathname } = useRouter()
  const memoryUuid = query.memoryUuid as string | undefined
  const animationUuid = query.animationUuid as string | undefined
  const muted = (isReady && query.muted === 'false' ? false : animationUuid ? true : undefined) as
    | boolean
    | undefined

  const [prev, setPrev] = useState<ViewedMemory | null>(null)
  const [next, setNext] = useState<ViewedMemory | null>(null)

  const viewedMemory: ViewedMemory = useMemo(
    () => ({
      memoryUuid,
      animationUuid,
    }),
    [animationUuid, memoryUuid]
  )

  useEffect(() => {
    if (all?.length && viewedMemory.memoryUuid) {
      const index = all?.findIndex(
        (item) =>
          item.memoryUuid === viewedMemory.memoryUuid &&
          item.animationUuid === viewedMemory.animationUuid
      )
      const prev = index <= 0 ? null : all[index - 1]
      const next = index >= all.length - 1 || index < 0 ? null : all[index + 1]
      setPrev(prev?.memoryUuid ? prev : null)
      setNext(next?.memoryUuid ? next : null)
    }
  }, [all, viewedMemory.animationUuid, viewedMemory.memoryUuid])

  const setViewedMemory = useCallback(
    async (memory: ViewedMemory) => {
      await replace(
        encodeQuery(pathname, {
          ...query,
          ...memory,
          muted: memory.animationUuid ? true : undefined,
        }),
        undefined,
        { shallow: true }
      )
    },
    [pathname, query, replace]
  )

  const goToNextMemory = useCallback(async () => {
    if (next) await setViewedMemory(next)
  }, [next, setViewedMemory])

  const goToPrevMemory = useCallback(async () => {
    if (prev) await setViewedMemory(prev)
  }, [prev, setViewedMemory])

  const clearViewedMemory = useCallback(async () => {
    if (memoryUuid || animationUuid) {
      const clearedQuery = omit(query, ['animationUuid', 'memoryUuid', 'muted'])

      await replace(encodeQuery(pathname, { ...clearedQuery }), undefined, {
        shallow: true,
      })
    }
  }, [animationUuid, memoryUuid, pathname, query, replace])

  const setMuted = useCallback(
    async (muted: boolean) => {
      await replace(encodeQuery(pathname, { ...query, muted }), undefined, { shallow: true })
    },
    [pathname, query, replace]
  )

  const toggleMuted = useCallback(() => setMuted(!muted), [muted, setMuted])

  return {
    viewedMemory,
    setViewedMemory,
    clearViewedMemory,
    goToNextMemory: next ? goToNextMemory : null,
    goToPrevMemory: prev ? goToPrevMemory : null,
    next,
    prev,
    muted,
    setMuted,
    toggleMuted,
  }
}

export const useSingingPortraitSongName = (selected?: SelectedMemory) => {
  const songsSwr = useContentfulSingingPortraitSongs()
  const selectedSong = songsSwr.data?.find(
    (s) => s?.driverId === selected?.selectedAnimation?.driver_type
  )

  return selectedSong?.songName || ''
}
